import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { CustomerExternalNumberType } from 'customer/enums';
import { useGetExternalCustomerNumbers } from 'customer/hooks';
import { ICustomerExternalNumber, ICustomerExternalNumberPayload } from 'customer/types';

import { getExternalCustomerNumberLabel, getExternalCustomerNumberTypeChoices } from './utils';

interface IProps {
  requestPayload?: ICustomerExternalNumberPayload;
  required?: boolean;
  enabled?: boolean;
  disabled?: boolean;
  typeChoiceEnabled?: boolean;
}

export const ExternalCustomerNumberField = ({
  requestPayload = {},
  required = false,
  enabled = false,
  disabled = false,
  typeChoiceEnabled = true,
}: IProps) => {
  const [enabledOnFocus, setEnabledOnFocus] = useState(enabled);
  const [selectedCustomerNumber, setSelectedCustomerNumber] = useState<
    ICustomerExternalNumber & { isNew?: boolean }
  >();

  const { t } = useTranslation();
  const { control, errors, setValue } = useFormContext();

  const { records } = useGetExternalCustomerNumbers(requestPayload, enabledOnFocus);

  useEffect(() => {
    if (disabled) {
      setValue('customerNumber', null);
      setValue('customerNumberType', CustomerExternalNumberType.ORDER);
    }
  }, [disabled, setValue]);

  const choices = getExternalCustomerNumberTypeChoices(records);

  const showExternalCustomerNumberTypeChoices =
    typeChoiceEnabled && selectedCustomerNumber?.isNew && choices.length > 0;

  return (
    <>
      <FormField
        name="customerNumber"
        type={InputType.AUTOCOMPLETE}
        inputProps={{
          control,
          error: errors?.customerNumber,
          optionKey: 'externalCustomerNumber',
          label: t('labelCustomerNumber'),
          defaultOptions: records,
          getLabel: (option: ICustomerExternalNumber) => getExternalCustomerNumberLabel(option),
          getKey: (option: ICustomerExternalNumber) =>
            `${option.source}|${option.externalCustomerNumber}`,
          onChange: (_, option: ICustomerExternalNumber) => setSelectedCustomerNumber(option),
          onFocus: () => setEnabledOnFocus(true),
          value: selectedCustomerNumber,
          hasCreateOption: true,
          enabledOnFocus: false,
          required,
          disabled,
        }}
      />

      {showExternalCustomerNumberTypeChoices && (
        <FormField
          name="customerNumberType"
          type={InputType.RADIO_GROUP}
          inputProps={{
            control,
            error: errors?.customerNumberType,
            defaultValue: CustomerExternalNumberType.ORDER,
            choices,
          }}
        />
      )}
    </>
  );
};
